/**
 * Identity service user and role administration (ADR-0053). Contracts follow
 * platform/services/identity (users.py, api.py): tenant-admin manages its own tenant,
 * platform-admin any tenant; the service and OPA enforce every rule, the console mirrors them.
 */
import type { Http } from "./http";
import type { Classification } from "./types";

const enc = encodeURIComponent;

export type UserLocale = "ar" | "en";
export type RoleArea = "work" | "build" | "govern" | "observe" | "channels" | "platform";
export const ROLE_AREAS: readonly RoleArea[] = ["work", "build", "govern", "observe", "channels", "platform"];

/** `UserOut`. `roles` are the user's direct realm roles (service roles included, never editable). */
export interface PlatformUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string | null;
  enabled: boolean;
  tenant: string;
  clearance: Classification;
  locale: UserLocale | null;
  roles: string[];
  created_at: string | null;
}

/** `UserCreated`: the generated temporary password is returned once. */
export interface PlatformUserCreated extends PlatformUser {
  temporary_password: string | null;
}

export interface UserPage {
  items: PlatformUser[];
  total: number;
  first: number;
  max: number;
}

export interface UserCreateIn {
  username: string;
  email?: string | null;
  first_name: string;
  last_name: string;
  tenant?: string | null;
  clearance: Classification;
  locale: UserLocale;
  enabled?: boolean;
  roles: string[];
  temporary_password?: string | null;
}

export interface UserUpdateIn {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  enabled?: boolean;
  clearance?: Classification;
  locale?: UserLocale;
}

/** `RoleOut`: a role an administrator may grant; `platform_only` roles need platform-admin. */
export interface AssignableRole {
  name: string;
  description: string;
  area: RoleArea;
  platform_only: boolean;
}

export interface PasswordResetOut {
  user_id: string;
  temporary: boolean;
  temporary_password: string | null;
}

export interface UserQuery {
  search?: string;
  first?: number;
  max?: number;
  tenant?: string;
}

export function usersApi(http: Http) {
  return {
    list: (q: UserQuery = {}) => http.get<UserPage>("identity", "/v1/users", { ...q }),
    get: (id: string) => http.get<PlatformUser>("identity", `/v1/users/${enc(id)}`),
    create: (body: UserCreateIn) => http.post<PlatformUserCreated>("identity", "/v1/users", body),
    update: (id: string, body: UserUpdateIn) => http.patch<PlatformUser>("identity", `/v1/users/${enc(id)}`, body),
    setRoles: (id: string, roles: string[]) => http.put<PlatformUser>("identity", `/v1/users/${enc(id)}/roles`, { roles }),
    resetPassword: (id: string) => http.post<PasswordResetOut>("identity", `/v1/users/${enc(id)}/reset-password`, {}),
    roles: () => http.get<AssignableRole[]>("identity", "/v1/roles"),
  };
}

export type UsersApi = ReturnType<typeof usersApi>;
